import { useState, useEffect, useCallback } from 'react';
import QRCode from 'react-qr-code';
import { 
  QrCodeIcon, 
  ShareIcon, 
  ClipboardDocumentIcon,
  PrinterIcon,
  ArrowPathIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

interface QRCardProps {
  shopId: string;  // This is now the ULID (public_id), not database ID
  shopName?: string;
}

const QRCard: React.FC<QRCardProps> = ({ shopId, shopName = 'Print Shop' }) => {
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate the upload URL using ULID
  const uploadUrl = `${window.location.origin}/send?shop=${shopId}`;
  const qrValue = uploadUrl;

  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(uploadUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      setError('Failed to copy to clipboard');
      setTimeout(() => setError(null), 3000);
    }
  }, [uploadUrl]);

  const shareOnWhatsApp = useCallback(() => {
    const message = `Hi! Now you can send documents for printing directly to my shop "${shopName}" via this link: ${uploadUrl}\n\nFast, secure, and easy!`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
  }, [shopName, uploadUrl]);

  const shareUrl = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Send Documents to ${shopName}`,
          text: 'Upload your documents here for printing',
          url: uploadUrl
        });
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Failed to share:', error);
          copyToClipboard(); // Fallback to copy
        }
      }
    } else {
      copyToClipboard();
    }
  }, [uploadUrl, shopName, copyToClipboard]);

  const downloadQR = useCallback(() => {
    const svg = document.getElementById('qr-code-svg');
    if (!svg) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Scale factor for high DPI
    const scale = 2;
    const padding = 60 * scale;
    const qrSize = 280 * scale;
    const logoSize = 40 * scale;
    const headerHeight = 140 * scale; 
    const footerHeight = 80 * scale;
    const totalHeight = headerHeight + qrSize + footerHeight + (padding * 2);

    canvas.width = qrSize + (padding * 2);
    canvas.height = totalHeight;

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add border
    ctx.strokeStyle = '#2563eb';
    ctx.lineWidth = 4 * scale;
    ctx.strokeRect(10 * scale, 10 * scale, canvas.width - (20 * scale), canvas.height - (20 * scale));

    // Load logo
    const logoImg = new Image();
    logoImg.crossOrigin = "Anonymous";
    logoImg.src = '/logo-icon.svg';
    
    const drawContent = () => {
      if (logoImg.complete && logoImg.naturalHeight !== 0) {
        const logoX = (canvas.width - logoSize) / 2;
        ctx.drawImage(logoImg, logoX, 30 * scale, logoSize, logoSize);
      }

      ctx.fillStyle = '#1f2937';
      ctx.font = `bold ${28 * scale}px Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText('Prindox', canvas.width / 2, 90 * scale);

      ctx.fillStyle = '#2563eb';
      ctx.font = `bold ${22 * scale}px Arial, sans-serif`;
      ctx.fillText(shopName, canvas.width / 2, 120 * scale);

      const clonedSvg = svg.cloneNode(true) as SVGElement;
      clonedSvg.setAttribute('width', qrSize.toString());
      clonedSvg.setAttribute('height', qrSize.toString());
      clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      
      const svgData = new XMLSerializer().serializeToString(clonedSvg);
      const img = new Image();
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      img.onload = () => {
        const qrX = padding;
        const qrY = headerHeight + padding;
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(img, qrX, qrY, qrSize, qrSize);
        ctx.imageSmoothingEnabled = true;

        // Draw white rounded background container for logo in center of QR
        const logoOnQrSize = 52 * scale;
        const logoOnQrX = qrX + (qrSize - logoOnQrSize) / 2;
        const logoOnQrY = qrY + (qrSize - logoOnQrSize) / 2;
        const radius = 10 * scale;

        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(logoOnQrX - (4 * scale), logoOnQrY - (4 * scale), logoOnQrSize + (8 * scale), logoOnQrSize + (8 * scale), radius);
        } else {
          ctx.rect(logoOnQrX - (4 * scale), logoOnQrY - (4 * scale), logoOnQrSize + (8 * scale), logoOnQrSize + (8 * scale));
        }
        ctx.fill();

        // Draw logo
        if (logoImg.complete && logoImg.naturalHeight !== 0) {
          ctx.drawImage(logoImg, logoOnQrX, logoOnQrY, logoOnQrSize, logoOnQrSize);
        }

        URL.revokeObjectURL(url);

        ctx.fillStyle = '#4b5563';
        ctx.font = `${16 * scale}px Arial, sans-serif`;
        const instructionY = qrY + qrSize + (30 * scale);
        ctx.fillText('Scan to Upload Documents', canvas.width / 2, instructionY);

        ctx.fillStyle = '#6b7280';
        ctx.font = `${11 * scale}px Arial, sans-serif`;
        ctx.fillText(uploadUrl, canvas.width / 2, instructionY + (25 * scale));

        ctx.fillStyle = '#9ca3af';
        ctx.font = `${14 * scale}px Arial, sans-serif`;
        ctx.fillText('Powered by Prindox', canvas.width / 2, canvas.height - (25 * scale));

        try {
            const pngFile = canvas.toDataURL('image/png');
            const downloadLink = document.createElement('a');
            downloadLink.download = `${shopName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_qr.png`;
            downloadLink.href = pngFile;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        } catch (e) {
            setError('Failed to generate image');
        }
      };
      
      img.src = url;
    };

    if (logoImg.complete) drawContent(); else logoImg.onload = drawContent;
  }, [shopName, uploadUrl]);

  const printQR = useCallback(() => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - ${shopName}</title>
          <style>
            @page { size: A4; margin: 0; }
            body { 
              margin: 0; 
              padding: 0; 
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              font-family: 'Segoe UI', system-ui, sans-serif;
              background: #f3f4f6;
            }
            .poster {
              width: 170mm;
              height: 240mm;
              background: white;
              padding: 20mm;
              box-shadow: 0 10px 25px rgba(0,0,0,0.1);
              display: flex;
              flex-direction: column;
              align-items: center;
              border-radius: 20px;
              position: relative;
              border: 5px solid #2563eb;
            }
            .logo-section {
              display: flex;
              align-items: center;
              margin-bottom: 30px;
            }
            .logo { width: 60px; height: 60px; margin-right: 15px; }
            .brand { font-size: 48px; font-weight: 900; color: #1f2937; letter-spacing: -1px; }
            .shop-name {
              font-size: 36px;
              font-weight: 800;
              color: #2563eb;
              margin-bottom: 10px;
              text-align: center;
            }
            .instruction-large {
              font-size: 42px;
              font-weight: 900;
              color: #111827;
              margin: 30px 0;
              text-align: center;
              line-height: 1.1;
            }
            .qr-wrapper {
              background: #f8fafc;
              padding: 40px;
              border-radius: 30px;
              border: 3px dashed #cbd5e1;
              margin-bottom: 30px;
              position: relative;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .qr-code { width: 300px; height: 300px; position: relative; }
            .qr-logo-overlay {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 64px;
              height: 64px;
              background: white;
              border-radius: 14px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.08);
              display: flex;
              align-items: center;
              justify-content: center;
              border: 2.5px solid #f1f5f9;
            }
            .qr-logo-overlay img {
              width: 44px;
              height: 44px;
            }
            .steps {
              display: flex;
              justify-content: space-between;
              width: 100%;
              margin-top: 20px;
            }
            .step {
              flex: 1;
              text-align: center;
              padding: 10px;
            }
            .step-num {
              width: 30px;
              height: 30px;
              background: #2563eb;
              color: white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: bold;
              margin: 0 auto 10px;
            }
            .step-text { font-size: 14px; font-weight: 600; color: #4b5563; }
            .footer {
              position: absolute;
              bottom: 30px;
              font-size: 18px;
              color: #9ca3af;
              font-weight: 600;
            }
            @media print {
              body { background: white; padding: 0; }
              .poster { box-shadow: none; border: 8px solid #2563eb; width: 100%; height: 100%; border-radius: 0; }
            }
          </style>
        </head>
        <body>
          <div class="poster">
            <div class="logo-section">
              <img src="${window.location.origin}/logo-icon.svg" class="logo" />
              <div class="brand">Prindox</div>
            </div>
            <div class="shop-name">${shopName}</div>
            <div class="instruction-large">SCAN TO UPLOAD & PRINT<br>INSTANTLY</div>
            <div class="qr-wrapper">
              <div id="qr-code-container" class="qr-code">
                <div class="qr-logo-overlay">
                  <img src="${window.location.origin}/logo-icon.svg" />
                </div>
              </div>
            </div>
            <div class="steps">
              <div class="step">
                <div class="step-num">1</div>
                <div class="step-text">Scan QR</div>
              </div>
              <div class="step">
                <div class="step-num">2</div>
                <div class="step-text">Upload Files</div>
              </div>
              <div class="step">
                <div class="step-num">3</div>
                <div class="step-text">Print & Collect</div>
              </div>
            </div>
            <div class="footer">www.prindox.com</div>
          </div>
          <script>
            window.addEventListener('load', function() {
              setTimeout(() => {
                window.print();
                window.close();
              }, 500);
            });
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();

    const qrContainer = printWindow.document.getElementById('qr-code-container');
    if (qrContainer) {
      const qrSvg = document.getElementById('qr-code-svg')?.cloneNode(true) as SVGElement;
      if (qrSvg) {
        qrSvg.setAttribute('width', '100%');
        qrSvg.setAttribute('height', '100%');
        qrContainer.appendChild(qrSvg);
      }
    }
  }, [shopName, uploadUrl]);

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8">
      <div className="text-center">
        <div className="flex items-center justify-center mb-6">
          <div className="p-3 bg-blue-50 rounded-xl">
            <QrCodeIcon className="h-8 w-8 text-blue-600" />
          </div>
          <div className="ml-4 text-left">
            <h3 className="text-xl font-bold text-gray-900">Shop QR Code</h3>
            <p className="text-sm text-gray-500">Your digital storefront link</p>
          </div>
        </div>
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-600 font-medium">{error}</p>
          </div>
        )}
        
        <div className="bg-white p-6 rounded-3xl border-2 border-dashed border-gray-200 mb-8 relative group flex flex-col items-center justify-center">
          <div className="relative">
            <QRCode
              id="qr-code-svg"
              value={qrValue}
              size={220}
              level="H"
              className="mx-auto transition-transform duration-300 group-hover:scale-105"
            />
            {/* Center Logo Icon Overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-12 h-12 bg-white rounded-xl shadow-md p-1 border border-slate-100 flex items-center justify-center">
                <img src="/logo-icon.svg" alt="Prindox Logo" className="w-9 h-9" />
              </div>
            </div>
          </div>
          <div className="mt-4 text-xs font-bold text-blue-600 tracking-widest uppercase">
            Scan to upload
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={shareOnWhatsApp}
              className="flex items-center justify-center px-6 py-4 bg-green-500 text-white rounded-2xl hover:bg-green-600 transition-all shadow-lg hover:shadow-green-200 active:scale-95"
            >
              <ChatBubbleLeftRightIcon className="h-6 w-6 mr-2" />
              <span className="font-bold">Share on WhatsApp</span>
            </button>
            
            <button
              onClick={shareUrl}
              className="flex items-center justify-center px-6 py-4 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 transition-all shadow-lg hover:shadow-blue-200 active:scale-95"
            >
              <ShareIcon className="h-6 w-6 mr-2" />
              <span className="font-bold">Share Link</span>
            </button>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
            <button
              onClick={downloadQR}
              className="flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-semibold"
            >
              <ArrowPathIcon className="h-5 w-5 mr-2" />
              Download
            </button>
            
            <button
              onClick={printQR}
              className="flex items-center justify-center px-4 py-3 bg-indigo-50 text-indigo-700 rounded-xl hover:bg-indigo-100 transition-all font-bold border border-indigo-200"
            >
              <PrinterIcon className="h-5 w-5 mr-2" />
              Print Poster
            </button>
        </div>

        <button
          onClick={copyToClipboard}
          className="w-full mt-4 py-2 text-sm text-gray-500 hover:text-blue-600 transition-colors flex items-center justify-center"
        >
          <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
          {copied ? 'Copied to clipboard!' : 'Copy direct link'}
        </button>
      </div>
    </div>
  );
};

export default QRCard;